import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styles from "./Masthead.module.css";
import Icon from "./Icons";
import { routes } from "../data/content";
import { servicesByCategory } from "../data/services";
import useBlogPosts from "../hooks/useBlogPosts";
import useSiteSettings from "../hooks/useSiteSettings";

const physioChildren = servicesByCategory("physiotherapy");

/**
 * One top-level item. Physiotherapy carries a dropdown: the label
 * itself stays a real link to the category page, and a separate
 * disclosure button opens the submenu — so the parent is reachable
 * by keyboard and pointer alike, which a hover-only menu is not.
 */
function NavItem({ route }) {
  const [open, setOpen] = useState(false);
  const itemRef = useRef(null);
  const closeTimer = useRef(null);

  const cancelClose = () => clearTimeout(closeTimer.current);
  // small grace period so the pointer can cross the gap to the panel
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => cancelClose(), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClickAway = (e) => {
      if (!itemRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickAway);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickAway);
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`;

  if (!route.hasDropdown) {
    return (
      <li className={styles.navItem}>
        <NavLink to={route.path} className={linkClass}>
          {route.label}
        </NavLink>
      </li>
    );
  }

  const panelId = "nav-physiotherapy";

  return (
    <li
      className={styles.navItem}
      ref={itemRef}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      /* focus moving anywhere inside keeps it open for keyboard users */
      onFocus={() => {
        cancelClose();
        setOpen(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <div className={styles.navParent}>
        <NavLink to={route.path} className={linkClass}>
          {route.label}
        </NavLink>
        <button
          type="button"
          className={`${styles.disclosure} ${open ? styles.disclosureOpen : ""}`}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={`${open ? "Hide" : "Show"} ${route.label} services`}
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name="chevron" size={15} />
        </button>
      </div>

      {/* dims the page behind the open panel so it reads as a
          deliberate overlay rather than content going missing.
          onClick closes it directly — the scrim sits inside itemRef,
          so the outside-click handler alone treats it as "inside". */}
      <div
        className={styles.scrim}
        hidden={!open}
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />

      <div id={panelId} className={styles.panel} hidden={!open}>
        <ul className={styles.panelList}>
          {physioChildren.map((service) => (
            <li key={service.slug}>
              <NavLink
                to={`/physiotherapy/${service.slug}`}
                className={({ isActive }) =>
                  `${styles.panelLink} ${isActive ? styles.panelLinkActive : ""}`
                }
              >
                <span className={styles.panelIcon}>
                  <Icon name={service.icon} size={19} />
                </span>
                <span className={styles.panelTitle}>{service.title}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

export default function Masthead() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();

  // Blogs only appears once there is something to read — a nav item
  // leading to an empty page is worse than no nav item.
  const { hasPosts } = useBlogPosts();
  const { doctor } = useSiteSettings();
  const visibleRoutes = routes.filter((r) => !r.requiresPosts || hasPosts);

  useEffect(() => {
    setMenuOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className={styles.bar}>
        <div className={`u-shell ${styles.inner}`}>
          <NavLink to="/" className={styles.mark}>
            <span className={styles.markGlyph} aria-hidden="true">
              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor">
                <path d="M16 5v22" strokeWidth="1.4" strokeLinecap="round" />
                <path
                  d="M11 9h10M10.5 14h11M11 19h10M12 24h8"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={styles.markText}>
              <strong>Heal with Dr. Aakash</strong>
              <span>Physiotherapy &middot; Gurugram</span>
            </span>
          </NavLink>

          <nav className={styles.nav} aria-label="Primary">
            <ul className={styles.navList}>
              {visibleRoutes.map((route) => (
                <NavItem key={route.path} route={route} />
              ))}
            </ul>
          </nav>

          <div className={styles.actions}>
            <NavLink to="/contact" className={styles.cta}>
              Book a Consultation
            </NavLink>
            <button
              type="button"
              className={styles.menuBtn}
              onClick={() => setMenuOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={menuOpen}
            >
              <Icon name="menu" size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Rendered outside <header>: the bar's backdrop-filter makes it a
          containing block for fixed children, which would trap the sheet
          inside the bar instead of covering the viewport. */}
      <div
        className={`${styles.sheet} ${menuOpen ? styles.sheetOpen : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        hidden={!menuOpen}
      >
        <div className={styles.sheetHead}>
          <span className={styles.sheetTitle}>Menu</span>
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation menu"
          >
            <Icon name="close" size={22} />
          </button>
        </div>

        <nav className={styles.sheetNav} aria-label="Mobile">
          {visibleRoutes.map((route) => {
            if (!route.hasDropdown) {
              return (
                <NavLink
                  key={route.path}
                  to={route.path}
                  className={({ isActive }) =>
                    `${styles.sheetLink} ${isActive ? styles.sheetLinkActive : ""}`
                  }
                >
                  {route.label}
                </NavLink>
              );
            }

            const expanded = openGroup === route.path;
            return (
              <div key={route.path} className={styles.sheetGroup}>
                <div className={styles.sheetGroupHead}>
                  <NavLink
                    to={route.path}
                    className={({ isActive }) =>
                      `${styles.sheetLink} ${styles.sheetGroupLink} ${
                        isActive ? styles.sheetLinkActive : ""
                      }`
                    }
                  >
                    {route.label}
                  </NavLink>
                  <button
                    type="button"
                    className={`${styles.sheetToggle} ${
                      expanded ? styles.sheetToggleOpen : ""
                    }`}
                    aria-expanded={expanded}
                    aria-controls="sheet-physio"
                    aria-label={`${expanded ? "Hide" : "Show"} ${route.label} services`}
                    onClick={() => setOpenGroup(expanded ? null : route.path)}
                  >
                    <Icon name="chevron" size={18} />
                  </button>
                </div>

                <ul id="sheet-physio" className={styles.sheetSub} hidden={!expanded}>
                  {physioChildren.map((service) => (
                    <li key={service.slug}>
                      <NavLink
                        to={`/physiotherapy/${service.slug}`}
                        className={styles.sheetSubLink}
                      >
                        {service.title}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Contact was dropped from the nav list — this is now the
            only mobile-menu route to /contact, so it stays a real
            link rather than a phone shortcut. */}
        <NavLink to="/contact" className={styles.sheetCta}>
          Book a Consultation
        </NavLink>
        <a href={`tel:${doctor.phoneHref}`} className={styles.sheetPhone}>
          <Icon name="phone" size={16} />
          {doctor.phone}
        </a>
      </div>
    </>
  );
}
